import { extractErrorDetails } from '../utils/extract-error-message';

export const isSensitiveEmailTemplateRequest = (url?: string): boolean => Boolean(url?.includes('/email-templates'));

const isEmailTemplateCatalogRequest = (url?: string): boolean => Boolean(url?.match(/\/email-templates\/catalog(?:[/?#]|$)/));

const EXPECTED_ENTITLEMENT_MESSAGES = new Set([
  'ENTITLEMENT_REQUIRED',
  'EMAIL_TEMPLATES.ERRORS.ENTITLEMENT_REQUIRED',
  'common.EMAIL_TEMPLATES.ERRORS.ENTITLEMENT_REQUIRED',
]);

export const isExpectedEmailTemplateEntitlementError = (input: { url?: string; status?: number; message?: unknown }): boolean => {
  if (!isEmailTemplateCatalogRequest(input.url) || input.status !== 403) return false;

  const { message, codes } = extractErrorDetails(input.message);
  return [message, ...codes].some((candidate) => candidate !== undefined && EXPECTED_ENTITLEMENT_MESSAGES.has(candidate));
};

/**
 * GET /logs/trace/:traceId y GET /logs/traces responden 404 cuando la traza no existe
 * o el backend aún no expone el listado: son resultados esperados manejados por la UI
 * (el visor muestra «no existe» o «no disponible»), no fallos que merezcan el toast genérico.
 */
export const isExpectedTraceNotFound = (input: { url?: string; status?: number }): boolean =>
  input.status === 404 && Boolean(input.url?.match(/(?:^|\/)logs\/traces?(?:\/[^/?#]+)?(?:[?#]|$)/));

export const REDACTED = '[REDACTED]';

/**
 * Fragmentos que, presentes en el nombre normalizado de un campo, lo marcan
 * como secreto. La redacción es por NOMBRE DE CAMPO, no por ruta: una denylist
 * de rutas siempre llega tarde — `/auth/signin` no estaba en ninguna y por eso
 * cada login imprimía la contraseña en claro en la consola del navegador.
 *
 * Sobre-redactar es barato (un log menos legible); infra-redactar es una fuga de
 * credenciales, así que los fragmentos son deliberadamente amplios.
 */
const SENSITIVE_KEY_FRAGMENTS = [
  'password',
  'passwd',
  'passphrase',
  'secret',
  'token',
  'apikey',
  'privatekey',
  'credential',
  'authorization',
  'pfx',
  'p12',
  'otpcode',
];

/** Nombres que no contienen ningún fragmento anterior pero también son secretos. */
const SENSITIVE_KEY_EXACT = new Set(['pin', 'otp', 'jwt', 'auth', 'cookie', 'setcookie']);

/**
 * `new_password`, `newPassword` y `New-Password` son el mismo campo: se comparan
 * en minúsculas y sin separadores para que ninguna variante de estilo escape.
 */
const normalizeKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

export const isSensitiveFieldName = (key: string): boolean => {
  const normalized = normalizeKey(key);
  if (SENSITIVE_KEY_EXACT.has(normalized)) return true;
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
};

/** Profundidad máxima; más allá se corta para no recorrer estructuras patológicas. */
const MAX_DEPTH = 8;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const describeBinary = (value: Blob | ArrayBuffer): string => {
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    const name = typeof File !== 'undefined' && value instanceof File ? value.name : 'blob';
    return `[Binary ${name} ${value.size}B]`;
  }
  return `[Binary ${(value as ArrayBuffer).byteLength}B]`;
};

const redactFormData = (formData: FormData): Record<string, unknown> => {
  const entries: Record<string, unknown> = {};

  formData.forEach((value, key) => {
    if (isSensitiveFieldName(key)) {
      entries[key] = REDACTED;
      return;
    }
    entries[key] = typeof value === 'string' ? value : describeBinary(value);
  });

  return entries;
};

const redactValue = (value: unknown, depth: number, seen: WeakSet<object>): unknown => {
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return '[Truncated]';

  if (typeof FormData !== 'undefined' && value instanceof FormData) return redactFormData(value);
  if ((typeof Blob !== 'undefined' && value instanceof Blob) || value instanceof ArrayBuffer) return describeBinary(value);
  if (value instanceof Date) return value;

  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, depth + 1, seen));
  }

  const source = value as Record<string, unknown>;
  const output: Record<string, unknown> = {};

  for (const key of Object.keys(source)) {
    output[key] = isSensitiveFieldName(key) ? REDACTED : redactValue(source[key], depth + 1, seen);
  }

  return output;
};

/**
 * Devuelve una copia del payload con todo campo sensible sustituido por
 * `[REDACTED]`, a cualquier profundidad, en objetos, arrays, `FormData` y
 * cuerpos ya serializados a JSON. Nunca muta la entrada: el interceptor loguea
 * la copia y envía el original intacto.
 */
export const redactSensitiveData = (data: unknown): unknown => {
  if (typeof data === 'string') {
    // Axios ejecuta los interceptores de request antes de `transformRequest`,
    // así que aquí el cuerpo suele ser el objeto original. Si alguien serializa
    // a mano, se redacta igual en vez de dejar pasar el JSON en claro.
    try {
      const parsed: unknown = JSON.parse(data);
      if (typeof parsed !== 'object' || parsed === null) return data;
      return redactValue(parsed, 0, new WeakSet());
    } catch {
      return data;
    }
  }

  return redactValue(data, 0, new WeakSet());
};

/**
 * Redacción de un payload de log: aplica la denylist de campos y además borra
 * el cuerpo entero en las rutas de plantillas de correo, que son sensibles por
 * ruta y no por nombre de campo.
 */
export const redactLoggablePayload = (url: string | undefined, data: unknown): unknown =>
  isSensitiveEmailTemplateRequest(url) ? REDACTED : redactSensitiveData(data);
